import { makeAutoObservable, runInAction } from 'mobx'
import { type ReactNode } from 'react'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IDropdownItem, toastStore } from 'shared/ui'
import { getCurrencySymbol } from 'shared/lib/currency'
import { logger } from 'shared/lib'
import { Conversion, ConversionsApi, type IParamsConversion } from 'entities/Conversion'
import { ConversionEditorContent } from 'widgets/ConversionEditor/ui/ConversionEditorContent'
import { ConversionEditorActions } from 'widgets/ConversionEditor/ui/ConversionEditorActions'

export class ConversionEditorStore {
  private _onSuccess: (conversion: Conversion) => void
  private _editingConversion: Conversion | null = null
  private _isEditMode = false
  private _isSaving = false
  private _isClearing = false

  constructor({
    onSuccess,
    conversion,
  }: {
    onSuccess: (conversion: Conversion) => void
    conversion?: Conversion
  }) {
    this._onSuccess = onSuccess
    makeAutoObservable(this)

    if (conversion) {
      this._isEditMode = true
      this._editingConversion = conversion
      this.populateFromConversion(conversion)
      this.open()
    } else {
      this.open()
    }
  }
  id = nanoid()
  name = ''
  conversion_url = ''
  currency = 'USD'
  default_value = 0
  default_value_format = '$0.00'
  conversion_attribution = ''

  errorsMap: Map<keyof IParamsConversion, ReactNode> = new Map()

  private open = () => {
    modalStore.addModal({
      id: this.id,
      title: this._isEditMode ? 'Edit conversion' : 'New conversion',
      ModalContent: ConversionEditorContent,
      ModalActions: ConversionEditorActions,
      ModalContentProps: {
        store: this,
      },
      showCloseButton: false,
    })
  }

  onClose = () => {
    modalStore.removeModal(this.id)
  }

  setName = (value: string) => {
    this.name = value
  }
  setUrl = (value: string) => {
    this.conversion_url = value
  }
  setCurrency = (value: string) => {
    this.currency = value
  }

  setDefaultValue = (value: number, formated_value: string) => {
    this.default_value = value
    this.default_value_format = formated_value
  }

  setConversionType = (value: string) => {
    this.conversion_attribution = value
  }

  setError = (key: keyof IParamsConversion, value: ReactNode) => {
    this.errorsMap.set(key, value)
  }

  private populateFromConversion = (conversion: Conversion) => {
    this.name = conversion.name
    this.conversion_url = conversion.conversion_url
    this.currency = conversion.currency
    this.default_value = conversion.default_value
    this.default_value_format = `${getCurrencySymbol(
      conversion.currency
    )}${conversion.default_value.toFixed(2)}`
    this.conversion_attribution = conversion.conversion_attribution.toString()
  }

  private validateForm = () => {
    this.errorsMap.clear()
    if (!this.name.trim()) {
      this.setError('name', 'Please enter conversion name')
    }
    if (!this.conversion_url.trim()) {
      this.setError('conversion_url', 'Please enter a URL')
    }
    if (!this.conversion_attribution) {
      this.setError('conversion_attribution', 'Please select the conversion type')
    }
    return this.errorsMap.size === 0
  }

  private handleApiError = (e: unknown) => {
    if (e instanceof AxiosError) {
      const isConversionField = (key: string): key is keyof IParamsConversion => key in this.params
      e.errors.entries().forEach(([key, value]) => {
        if (isConversionField(key)) {
          this.setError(key, value.value)
        } else {
          toastStore.add({ type: 'error', title: value.value })
        }
      })
    }
    logger.error(e)
  }

  onCreate = async () => {
    if (!this.validateForm()) return

    runInAction(() => {
      this._isSaving = true
    })
    try {
      const { data } = await ConversionsApi.createConversion(this.params)
      this._onSuccess(new Conversion(data))
      modalStore.closeModal(this.id)
      toastStore.add({
        type: 'success',
        title: 'Conversion created',
      })
    } catch (e) {
      this.handleApiError(e)
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  onUpdate = async () => {
    if (!this.validateForm()) return

    if (!this._editingConversion?.id) {
      logger.error('No conversion ID available for update')
      return
    }

    runInAction(() => {
      this._isSaving = true
    })
    try {
      const { data } = await ConversionsApi.updateConversion(
        this._editingConversion.id,
        this.params
      )
      this._onSuccess(new Conversion(data))
      modalStore.closeModal(this.id)
      toastStore.add({
        type: 'success',
        title: 'Conversion updated',
      })
    } catch (e) {
      this.handleApiError(e)
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  onClearConversionData = async () => {
    if (!this._editingConversion?.id) {
      logger.error('No conversion ID available for clear conversion data')
      return
    }

    runInAction(() => {
      this._isClearing = true
    })
    try {
      await ConversionsApi.clearConversion(this._editingConversion.id)
      modalStore.closeModal(this.id)
      toastStore.add({
        type: 'success',
        title: 'Recorded conversion data cleared',
      })
    } catch (e) {
      this.handleApiError(e)
    } finally {
      runInAction(() => {
        this._isClearing = false
      })
    }
  }

  get params(): IParamsConversion {
    return {
      name: this.name,
      conversion_url: this.conversion_url,
      currency: this.currency,
      default_value: this.default_value,
      default_value_format: this.default_value_format,
      conversion_attribution: this.conversion_attribution,
    }
  }

  get currencySymbol() {
    return getCurrencySymbol(this.currency)
  }

  get currencyItems(): IDropdownItem[] {
    return [
      {
        id: 'USD',
        label: 'USD - United States Dollar',
      },
      {
        id: 'CAD',
        label: 'CAD - Canadian Dollar',
      },
    ]
  }

  get conversionTypeItems(): IDropdownItem[] {
    return [
      {
        id: '2',
        label: 'Count every conversion',
      },
      {
        id: '1',
        label: 'Count a max of one conversion per contact',
      },
    ]
  }

  get isEditMode() {
    return this._isEditMode
  }

  get isSaving() {
    return this._isSaving
  }

  get isClearing() {
    return this._isClearing
  }
}
